import { formatSportLabel, normalizeSportSlug } from "@clutch/auth/sports";
import {
	AmericanFootballIcon,
	BadmintonShuttleIcon,
	Billiard01Icon,
	Billiard02Icon,
	BoxingGloveIcon,
	FencingIcon,
	HockeyIcon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";
import {
	IconActivity, // Generic active
	IconBallBaseball,
	IconBallBasketball,
	IconBallFootball,
	IconBallTennis, // Badminton, Tennis, Squash
	IconBallVolleyball, // Volleyball, Handball, Netball
	IconBarbell,
	IconBike,
	IconBow,
	IconChessKnight,
	IconCricket,
	IconGolf,
	IconGymnastics,
	IconHorse, // Equestrian, Polo
	IconKarate,
	IconPingPong,
	IconRun,
	IconSailboat, // Sailing
	IconStretching, // Gymnastics
	IconSwimming,
	IconTarget, // Shooting
	IconTrophy,
	IconYoga, // Martial arts proxy
} from "@tabler/icons-react";
import type { ComponentType } from "react";

export type SportIconProps = {
	className?: string;
	size?: number | string;
	stroke?: number | string;
	strokeWidth?: number;
};

type SportIconComponent = ComponentType<SportIconProps>;

function createHugeSportIcon(icon: IconSvgElement): SportIconComponent {
	return ({ stroke, strokeWidth, ...props }) => (
		<HugeiconsIcon
			icon={icon}
			stroke={typeof stroke === "number" ? String(stroke) : stroke}
			strokeWidth={
				typeof strokeWidth === "string"
					? Number.parseFloat(strokeWidth)
					: strokeWidth
			}
			{...props}
		/>
	);
}

// Wrapper for Hugeicons to seamlessly behave like Tabler Icons across the UI
const HugeBadmintonIcon = createHugeSportIcon(BadmintonShuttleIcon);
const HugeBoxingIcon = createHugeSportIcon(BoxingGloveIcon);
const HugeFencingIcon = createHugeSportIcon(FencingIcon);
const HugeHockeyIcon = createHugeSportIcon(HockeyIcon);
const HugeBilliardsIcon2 = createHugeSportIcon(Billiard02Icon);
const HugeBilliardsIcon = createHugeSportIcon(Billiard01Icon);
const HugeAmericanFootballIcon = createHugeSportIcon(AmericanFootballIcon);

export type SportConfig = {
	id: string;
	label: string;
	icon: SportIconComponent;
	colorConfig: {
		text: string;
		bgLight: string;
		border: string;
	};
};

const themeClean = {
	text: "text-foreground",
	bgLight: "bg-accent/50",
	border: "border-border/50",
};

export const SPORTS_MAP: Record<string, SportConfig> = {
	archery: {
		id: "archery",
		label: "Archery",
		icon: IconBow,
		colorConfig: themeClean,
	},
	athletics: {
		id: "athletics",
		label: "Athletics",
		icon: IconRun,
		colorConfig: themeClean,
	},
	badminton: {
		id: "badminton",
		label: "Badminton",
		icon: HugeBadmintonIcon,
		colorConfig: themeClean,
	},
	baseball: {
		id: "baseball",
		label: "Baseball",
		icon: IconBallBaseball,
		colorConfig: themeClean,
	},
	basketball: {
		id: "basketball",
		label: "Basketball",
		icon: IconBallBasketball,
		colorConfig: themeClean,
	},
	billiards: {
		id: "billiards",
		label: "Billiards",
		icon: HugeBilliardsIcon2,
		colorConfig: themeClean,
	},
	boxing: {
		id: "boxing",
		label: "Boxing",
		icon: HugeBoxingIcon,
		colorConfig: themeClean,
	},
	chess: {
		id: "chess",
		label: "Chess",
		icon: IconChessKnight,
		colorConfig: themeClean,
	},
	cricket: {
		id: "cricket",
		label: "Cricket",
		icon: IconCricket,
		colorConfig: themeClean,
	},
	cycling: {
		id: "cycling",
		label: "Cycling",
		icon: IconBike,
		colorConfig: themeClean,
	},
	equestrian: {
		id: "equestrian",
		label: "Equestrian",
		icon: IconHorse,
		colorConfig: themeClean,
	},
	fencing: {
		id: "fencing",
		label: "Fencing",
		icon: HugeFencingIcon,
		colorConfig: themeClean,
	},
	football: {
		id: "football",
		label: "Football",
		icon: IconBallFootball,
		colorConfig: themeClean,
	},
	golf: { id: "golf", label: "Golf", icon: IconGolf, colorConfig: themeClean },
	gymnastics: {
		id: "gymnastics",
		label: "Gymnastics",
		icon: IconGymnastics,
		colorConfig: themeClean,
	},
	handball: {
		id: "handball",
		label: "Handball",
		icon: IconBallVolleyball,
		colorConfig: themeClean,
	},
	hockey: {
		id: "hockey",
		label: "Hockey",
		icon: HugeHockeyIcon,
		colorConfig: themeClean,
	},
	judo: { id: "judo", label: "Judo", icon: IconYoga, colorConfig: themeClean },
	kabaddi: {
		id: "kabaddi",
		label: "Kabaddi",
		icon: IconActivity,
		colorConfig: themeClean,
	},
	karate: {
		id: "karate",
		label: "Karate",
		icon: IconKarate,
		colorConfig: themeClean,
	},
	"kho-kho": {
		id: "kho-kho",
		label: "Kho Kho",
		icon: IconRun,
		colorConfig: themeClean,
	},
	netball: {
		id: "netball",
		label: "Netball",
		icon: IconBallBasketball,
		colorConfig: themeClean,
	},
	polo: { id: "polo", label: "Polo", icon: IconHorse, colorConfig: themeClean },
	rowing: {
		id: "rowing",
		label: "Rowing",
		icon: IconSailboat,
		colorConfig: themeClean,
	},
	rugby: {
		id: "rugby",
		label: "Rugby",
		icon: HugeAmericanFootballIcon,
		colorConfig: themeClean,
	},
	sailing: {
		id: "sailing",
		label: "Sailing",
		icon: IconSailboat,
		colorConfig: themeClean,
	},
	shooting: {
		id: "shooting",
		label: "Shooting",
		icon: IconTarget,
		colorConfig: themeClean,
	},
	snooker: {
		id: "snooker",
		label: "Snooker",
		icon: HugeBilliardsIcon,
		colorConfig: themeClean,
	},
	squash: {
		id: "squash",
		label: "Squash",
		icon: IconBallTennis,
		colorConfig: themeClean,
	},
	swimming: {
		id: "swimming",
		label: "Swimming",
		icon: IconSwimming,
		colorConfig: themeClean,
	},
	"table-tennis": {
		id: "table-tennis",
		label: "Table Tennis",
		icon: IconPingPong,
		colorConfig: themeClean,
	},
	taekwondo: {
		id: "taekwondo",
		label: "Taekwondo",
		icon: IconKarate,
		colorConfig: themeClean,
	},
	tennis: {
		id: "tennis",
		label: "Tennis",
		icon: IconBallTennis,
		colorConfig: themeClean,
	},
	triathlon: {
		id: "triathlon",
		label: "Triathlon",
		icon: IconBike,
		colorConfig: themeClean,
	},
	volleyball: {
		id: "volleyball",
		label: "Volleyball",
		icon: IconBallVolleyball,
		colorConfig: themeClean,
	},
	weightlifting: {
		id: "weightlifting",
		label: "Weightlifting",
		icon: IconBarbell,
		colorConfig: themeClean,
	},
	wrestling: {
		id: "wrestling",
		label: "Wrestling",
		icon: IconStretching,
		colorConfig: themeClean,
	},
	wushu: {
		id: "wushu",
		label: "Wushu",
		icon: IconYoga,
		colorConfig: themeClean,
	},
};

export function getSportConfig(sportId: string): SportConfig {
	const normalizedSportId = normalizeSportSlug(sportId) ?? sportId.trim();

	return (
		SPORTS_MAP[normalizedSportId] || {
			id: normalizedSportId || sportId,
			label: formatSportLabel(normalizedSportId || sportId),
			icon: IconTrophy,
			colorConfig: themeClean,
		}
	);
}

export function getSportLabel(sportId: string) {
	return getSportConfig(sportId).label;
}

export const SPORTS_LIST = Object.values(SPORTS_MAP).sort((a, b) =>
	a.label.localeCompare(b.label),
);
export const SPORTS_SLUGS_ONLY = SPORTS_LIST.map((sport) => sport.id);
